import React, {useState} from 'react';
import AnimateHeight from 'react-animate-height';
import StarIcon from '@mui/icons-material/Star';
import {Rating} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {ProfilePreviousCampaign, ProfileProps} from '@/types/creatorProfile';
import CREATOR_PROFILE_TEST_IDS from '@/components/creator-profile/testIds';
import Link from '@/components/link';
import styles from './CampaignHistorySummary.module.scss';

const T = {
  title: 'campaign-history-title',
  campaigns: 'campaign-history-campaigns',
  avgRating: 'campaign-history-avg-rating',
  tier: 'campaign-history-tier',
  tierPlaceholder: 'campaign-history-tier-placeholder',
  ratingPlaceholder: 'campaign-history-rating-placeholder',
  seeDetails: 'campaign-history-see-details',
  hideDetails: 'campaign-history-hide-details',
} as const;

interface CampaignHistorySummaryProps {
  profile: ProfileProps;
  trackButtonPressed: (buttonName: string, overrideParams?: object) => void;
}

function StatCard({
  testID,
  icon,
  value,
  label,
}: {
  testID: string;
  icon: React.ReactNode;
  value: string | number;
  label: string;
}) {
  return (
    <div className={styles.statCard} data-testid={testID}>
      {icon}
      <div className={styles.statText}>
        <Label size="lg" color={designSystemToken('semantic.fg.primary')}>
          {value}
        </Label>
        <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
          {label}
        </Body>
      </div>
    </div>
  );
}

function CampaignRow({
  campaign,
  onClick,
}: {
  campaign: ProfilePreviousCampaign;
  onClick?: (campaign: ProfilePreviousCampaign) => void;
}) {
  const year = campaign.date
    ? new Date(campaign.date).getFullYear()
    : undefined;

  const subtitle = [campaign.brandName, year].filter(Boolean).join(' \u00B7 ');

  return (
    <Link
      to={`/campaigns/${campaign.id}`}
      target="_blank"
      onClick={() => onClick?.(campaign)}
    >
      <div className={styles.campaignRow}>
        <div className={styles.campaignInfo}>
          <Label size="md" color={designSystemToken('semantic.fg.primary')}>
            {campaign.name}
          </Label>
          {subtitle && (
            <Body
              size="sm"
              color={designSystemToken('semantic.fg.inverse-secondary')}
            >
              {subtitle}
            </Body>
          )}
        </div>
        {campaign.rating != null && (
          <Rating
            value={campaign.rating}
            readOnly
            size="small"
            precision={0.5}
            emptyIcon={<StarIcon style={{opacity: 0.55}} fontSize="inherit" />}
          />
        )}
      </div>
    </Link>
  );
}

function CampaignHistorySummary({
  profile,
  trackButtonPressed,
}: CampaignHistorySummaryProps) {
  const [isOpen, setIsOpen] = useState(false);
  const campaigns = profile.profilePreviousCampaigns || [];
  const campaignCount = campaigns.length;
  const avgRating = profile.rating ?? translate(T.ratingPlaceholder);
  const tierKey = profile.rating >= 4 ? 'Top performer' : null;

  return (
    <div
      className={styles.container}
      data-testid={CREATOR_PROFILE_TEST_IDS.CAMPAIGN_HISTORY}
    >
      <div className={styles.header}>
        <Headline size="sm">{translate(T.title)}</Headline>
      </div>

      <div className={styles.statCardsRow}>
        <StatCard
          testID={CREATOR_PROFILE_TEST_IDS.CAMPAIGN_HISTORY__STAT_CAMPAIGNS}
          icon={
            <Icon
              size="small"
              appearance="neutral"
              name="Navigation-Megaphone"
            />
          }
          value={campaignCount}
          label={translate(T.campaigns)}
        />
        <StatCard
          testID={CREATOR_PROFILE_TEST_IDS.CAMPAIGN_HISTORY__STAT_RATING}
          icon={
            <Icon
              size="small"
              appearance="neutral"
              name="Navigation-Star-Line"
            />
          }
          value={avgRating}
          label={translate(T.avgRating)}
        />
        {tierKey && (
          <StatCard
            testID={CREATOR_PROFILE_TEST_IDS.CAMPAIGN_HISTORY__STAT_TIER}
            icon={
              <Icon size="small" appearance="neutral" name="Actions-Accept" />
            }
            value={translate(tierKey)}
            label={translate(T.tier)}
          />
        )}
      </div>

      {campaignCount > 0 && (
        <>
          <AnimateHeight
            duration={300}
            height={isOpen ? 'auto' : 0}
            className={styles.body}
          >
            <div
              className={styles.campaignList}
              data-testid={CREATOR_PROFILE_TEST_IDS.CAMPAIGN_HISTORY__LIST}
            >
              {campaigns.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  onClick={(c) => {
                    trackButtonPressed('Click past campaign', {
                      campaign_id: c.id,
                    });
                  }}
                />
              ))}
            </div>
          </AnimateHeight>

          <button
            type="button"
            className={styles.toggleButton}
            data-testid={CREATOR_PROFILE_TEST_IDS.CAMPAIGN_HISTORY__TOGGLE}
            onClick={() => {
              const next = !isOpen;
              setIsOpen(next);
              trackButtonPressed(
                next
                  ? 'Campaign History: See details'
                  : 'Campaign History: Hide details'
              );
            }}
          >
            <Label size="md" color={designSystemToken('semantic.fg.primary')}>
              {isOpen ? translate(T.hideDetails) : translate(T.seeDetails)}
            </Label>
            <div className={isOpen ? styles.arrowUp : undefined}>
              <Icon
                size="small"
                appearance="neutral"
                name="Actions-Arrow-Down-Small"
              />
            </div>
          </button>
        </>
      )}
    </div>
  );
}

export default CampaignHistorySummary;
